<template>
  <LayoutDefault
    isShowFooter
    @saveForm="saveEventForm"
    :isPublish="false"
    :isHistory="false"
    :dateCreated="formatDateForDisplay(formData.DateCreated)"
    :isSaveDisabled="!isFormValid || !isFormChanged"
  >
    <section class="event-edit">
      <div class="event-edit__content content">
        <div class="header">
          <h2 class="title">Контент (текстовое наполнение и мультимедиа)</h2>
          <span class="line"></span>
        </div>

        <div class="content__item">
          <p class="content__item-name">Название группы обучения (заголовок страницы):</p>
          <input
            class="content__item-input"
            :class="{ 'error': !formData.Header }"
            type="text"
            placeholder="Введите название группы"
            v-model="formData.Header"
          />
          <span v-if="!formData.Header" class="error-message">Это поле обязательно для заполнения</span>
        </div>

        <div class="content__item">
          <p class="content__item-name">Ссылка (описание программы обучения):</p>
          <input
            class="content__item-input"
            :class="{ 'error': !formData.ExternalUrl }"
            type="text"
            placeholder="Добавьте ссылку"
            v-model="formData.ExternalUrl"
          />
          <span v-if="!formData.ExternalUrl" class="error-message">Это поле обязательно для заполнения</span>
        </div>

        <div class="content__info">
          <div class="content__item">
            <p class="content__item-name">Начало обучения:</p>
            <div class="date-input-wrapper">
              <IndicatorDate
                calendar
                :date="displayDateBegin"
                @change-date="(date) => updateDate('DateBegin', date)"
                :class="{ 'error': !formData.DateBegin }"
              />
              <span v-if="!formData.DateBegin" class="error-message">Это поле обязательно для заполнения</span>
            </div>
          </div>
          <div class="content__item">
            <p class="content__item-name">Конец обучения:</p>
            <div class="date-input-wrapper">
              <IndicatorDate
                calendar
                :date="displayDateEnd"
                @change-date="(date) => updateDate('DateEnd', date)"
                :class="{ 'error': !formData.DateEnd }"
              />
              <span v-if="!formData.DateEnd" class="error-message">Это поле обязательно для заполнения</span>
            </div>
          </div>
          <div class="content__item">
            <p class="content__item-name">Количество часов:</p>
            <div class="input-wrapper">
              <input
                class="content__item-input"
                :class="{ 'error': formData.Duration <= 0 }"
                type="number"
                placeholder="40"
                v-model.number="formData.Duration"
              />
              <span v-if="formData.Duration <= 0" class="error-message">Количество часов должно быть больше 0</span>
            </div>
          </div>
        </div>

        <div class="content__type">
          <p class="content__item-name">Тип мероприятия:</p>
            <label v-for="(item , i) in groupStatuses"
              :key=i
              :for=item.Id
              class="radio-label"
            >
            {{item.Name}}
            <input
              type="radio"
              v-model="formData.GroupStatusId"
              :id="item.Id"
              :value="item.Id"
            >
          </label>
        </div>

        <!-- Сообщения о валидации и неизмененных данных -->
        <div v-if="showValidationMessage" class="validation-messages">
          <div v-if="!isFormValid" class="validation-message error">
            ❌ Пожалуйста, заполните все обязательные поля
          </div>
          <div v-else-if="!isFormChanged" class="validation-message info">
            ℹ️ Данные не изменены
          </div>
        </div>
      </div>
    </section>
  </LayoutDefault>
</template>

<script>
import { ref, reactive, watch, computed, onBeforeMount } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import LayoutDefault from '@/layout/LayoutDefault'
import IndicatorDate from '@/components/ui/IndicatorDate.vue'

export default {
  name: 'GroupLearningEditorViewNew',
  components: {
    LayoutDefault,
    IndicatorDate
  },
  props: {
    initialData: {
      type: Object,
      default: () => ({})
    }
  },
  setup () {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()

    // Инициализация формы с данными из объекта
    const formData = reactive({
      Id: null,
      Header: '',
      Duration: 0,
      DateBegin: '',
      DateCreated: '',
      DateEnd: '',
      ExternalUrl: '',
      GroupStatusId: 7
    })

    // Для отладки (можно удалить)
    const showDebug = ref(false)
    const formattedData = ref(null)
    const showValidationMessage = ref(false)

    // Сохраняем исходные данные для сравнения
    const originalData = ref(null)

    onBeforeMount(() => {
      store.dispatch('setGetItemGroupe', route.params.id)
      store.dispatch('setGroupStatuses')
    })

    const itemGroupe = computed(() => {
      return store.getters.getGroupeInfo
    })

    const groupStatuses = computed(() => {
      return store.getters.getGroupStatuses
    })

    watch(itemGroupe, (newValue) => {
      if (newValue) {
        // Сохраняем исходные данные
        originalData.value = { ...newValue }

        // Обновляем каждое свойство отдельно
        formData.Id = newValue.Id || 0
        formData.Header = newValue.Header || ''
        formData.Duration = newValue.Duration || 0
        formData.DateBegin = newValue.DateBegin || ''
        formData.DateCreated = newValue.DateCreated || ''
        formData.DateEnd = newValue.DateEnd || ''
        formData.ExternalUrl = newValue.ExternalUrl || ''
        formData.GroupStatusId = newValue.GroupStatusId || 7
      }
    })

    // Функция для глубокого сравнения объектов
    const isEqual = (obj1, obj2) => {
      if (obj1 === obj2) return true
      if (obj1 == null || obj2 == null) return false
      if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return obj1 === obj2

      const keys1 = Object.keys(obj1)
      const keys2 = Object.keys(obj2)

      if (keys1.length !== keys2.length) return false

      for (const key of keys1) {
        if (!keys2.includes(key)) return false
        if (!isEqual(obj1[key], obj2[key])) return false
      }

      return true
    }

    // Проверяем, изменились ли данные формы
    const isFormChanged = computed(() => {
      if (!originalData.value) return true // Если нет исходных данных, считаем что форма изменена

      const currentFormData = {
        Header: formData.Header,
        Duration: formData.Duration,
        DateBegin: formData.DateBegin,
        DateEnd: formData.DateEnd,
        ExternalUrl: formData.ExternalUrl,
        GroupStatusId: formData.GroupStatusId
      }

      const originalFormData = {
        Header: originalData.value.Header,
        Duration: originalData.value.Duration,
        DateBegin: originalData.value.DateBegin,
        DateEnd: originalData.value.DateEnd,
        ExternalUrl: originalData.value.ExternalUrl,
        GroupStatusId: originalData.value.GroupStatusId
      }

      console.log('Сравнение данных:', {
        current: currentFormData,
        original: originalFormData,
        changed: !isEqual(currentFormData, originalFormData)
      })

      return !isEqual(currentFormData, originalFormData)
    })

    // Функция для преобразования даты в формат DD.MM.YYYY
    const formatDateForDisplay = (date) => {
      if (!date) return ''

      let dateObj

      // Если date уже является объектом Date
      if (date instanceof Date) {
        dateObj = date
      } else if (typeof date === 'string') {
        dateObj = new Date(date)
      } else if (typeof date === 'number') {
        dateObj = new Date(date)
      } else if (date && typeof date.getTime === 'function') {
        dateObj = date
      } else {
        console.warn('Неизвестный формат даты:', date)
        return ''
      }

      // Проверяем, что дата валидна
      if (isNaN(dateObj.getTime())) {
        console.warn('Невалидная дата:', date)
        return ''
      }

      // Форматируем в DD.MM.YYYY
      const day = String(dateObj.getDate()).padStart(2, '0')
      const month = String(dateObj.getMonth() + 1).padStart(2, '0')
      const year = dateObj.getFullYear()

      return `${day}.${month}.${year}`
    }

    // Функция для преобразования даты в формат для сервера (ISO с микросекундами)
    const formatDateToISO = (date) => {
      if (!date) return ''

      let dateObj

      // Если date уже является объектом Date
      if (date instanceof Date) {
        dateObj = date
      } else if (typeof date === 'string') {
        // Если дата в формате DD.MM.YYYY
        if (date.includes('.')) {
          const [day, month, year] = date.split('.')
          dateObj = new Date(year, month - 1, day)
        } else {
          dateObj = new Date(date)
        }
      } else if (typeof date === 'number') {
        dateObj = new Date(date)
      } else if (date && typeof date.getTime === 'function') {
        dateObj = date
      } else {
        console.warn('Неизвестный формат даты:', date)
        return ''
      }

      // Проверяем, что дата валидна
      if (isNaN(dateObj.getTime())) {
        console.warn('Невалидная дата:', date)
        return ''
      }

      // Форматируем в ISO строку с микросекундами
      const isoString = dateObj.toISOString()

      // Заменяем Z на нужное количество цифр микросекунд (7 цифр)
      return isoString.replace('Z', '0000Z').replace(/\.[0-9]{0,7}Z$/, '.0000000Z')
    }

    // Вычисляемые свойства для отображения дат в формате DD.MM.YYYY
    const displayDateBegin = computed(() => {
      return formatDateForDisplay(formData.DateBegin)
    })

    const displayDateEnd = computed(() => {
      return formatDateForDisplay(formData.DateEnd)
    })

    // Обновление дат с преобразованием формата
    const updateDate = (field, date) => {
      // date может быть в формате DD.MM.YYYY от пользователя или Date объектом
      const formattedDate = formatDateToISO(date)
      formData[field] = formattedDate
      console.log(`Дата ${field} обновлена:`, {
        полученная: date,
        для_отображения: formatDateForDisplay(date),
        для_сервера: formattedDate
      })
    }

    // Проверка валидности формы
    const isFormValid = computed(() => {
      return (
        formData.Header?.trim() !== '' &&
        formData.ExternalUrl?.trim() !== '' &&
        formData.DateBegin !== '' &&
        formData.DateEnd !== '' &&
        formData.Duration > 0
      )
    })

    // Вычисляемое свойство для получения данных в нужном формате для сервера
    const getFormattedFormData = computed(() => {
      return {
        Id: +route.params.id !== 0 ? formData.Id : 0,
        Header: formData.Header,
        Duration: formData.Duration,
        DateBegin: formData.DateBegin, // Уже в формате для сервера
        DateEnd: formData.DateEnd, // Уже в формате для сервера
        ExternalUrl: formData.ExternalUrl,
        GroupStatusId: formData.GroupStatusId,
        GroupStatusName: 'string'
      }
    })

    // Функция для получения данных (можно использовать для отправки на сервер)
    const getFormattedData = () => {
      formattedData.value = getFormattedFormData.value
      console.log('Отформатированные данные для отправки:', formattedData.value)
      return formattedData.value
    }

    const saveEventForm = async () => {
      // Всегда показываем сообщения при попытке сохранения
      showValidationMessage.value = true

      // Проверяем валидность формы
      if (!isFormValid.value) {
        console.log('Форма невалидна, отправка запрещена')
        return
      }

      // Проверяем, изменились ли данные
      if (!isFormChanged.value) {
        console.log('Данные не изменены, отправка запрещена')
        return
      }

      // Если форма валидна и данные изменены - сохраняем
      console.log('Форма валидна и данные изменены, отправляем...')

      try {
        if (+route.params.id !== 0) {
          await store.dispatch('setIUpdateItemGroupe', getFormattedData())
        } else {
          await store.dispatch('setCreateItemGroupe', getFormattedData())
        }

        showValidationMessage.value = false

        // Обновляем исходные данные после сохранения
        originalData.value = { ...formData }

        // Возвращаемся на предыдущую страницу после успешного сохранения
        console.log('Сохранение успешно, возвращаемся на предыдущую страницу')
        router.go(-1)
      } catch (error) {
        console.error('Ошибка при сохранении:', error)
        // Можно добавить обработку ошибки сохранения
      }
    }

    // Следим за изменением GroupStatusId
    watch(() => formData.GroupStatusId, (newValue) => {
      console.log('Тип мероприятия изменен на:', newValue)
    })

    // Если нужно реагировать на изменения формы
    watch(formData, (newValue) => {
      console.log('Данные формы обновлены:', newValue)
    }, { deep: true })

    return {
      formData,
      showDebug,
      formattedData,
      showValidationMessage,
      isFormValid,
      isFormChanged,
      displayDateBegin,
      displayDateEnd,
      updateDate,
      getFormattedData,
      getFormattedFormData,
      saveEventForm,
      formatDateForDisplay,
      groupStatuses
    }
  }
}
</script>

<style lang="scss" scoped>
.event-edit {
  display: flex;
}

.content {
  width: 100%;
  max-height: 790px;
  overflow: auto;

  &__item {
    width: 50%;
    margin-bottom: 24px;
    position: relative;

    &-name {
      margin-bottom: 8px;
      font-weight: 500;
      font-size: 18px;
      color: #000000;
      text-align: left;
    }

    &-input {
      width: 100%;
      height: 64px;
      padding: 10px 12px;
      border: 1px solid #000000;
      border-radius: 6px;
      overflow: auto;
      font-size: 18px;

      &::placeholder {
        font-size: 16px;
        font-weight: 400;
        color: #787878;
      }

      &.error {
        border-color: #ff4444;
        background-color: #fff8f8;
      }
    }
  }

  &__info {
    width: 50%;
    display: flex;
    align-items: flex-start;
    gap: 24px;

    input {
      height: 42px;
      padding: 10px 12px;
    }

    .content__item {
      width: 100%;
      margin-bottom: 40px;
    }
  }

  &__type {
    width: 50%;
    padding: 9px 12px;
    display: flex;
    flex-direction: column;
    align-items: start;
    font-weight: 400;
    font-size: 16px;
    border: 2px solid #787878;

    input {
      border: 2px solid #787878;
      width: 20px;
      height: 20px;
    }
  }
}

/* Обертки для полей ввода с абсолютным позиционированием ошибок */
.date-input-wrapper,
.input-wrapper {
  position: relative;
  width: 100%;
}

.error-message {
  position: absolute;
  left: 0;
  top: 100%;
  margin-top: 4px;
  color: #ff4444;
  font-size: 14px;
  display: block;
  white-space: nowrap;
}

/* Специфичные стили для полей ввода текста */
.content__item:not(.content__info .content__item) .error-message {
  position: absolute;
  left: 0;
  top: calc(100% + 4px);
}

/* Специфичные стили для полей в content__info */
.content__info {
  .date-input-wrapper,
  .input-wrapper {
    .error-message {
      position: absolute;
      left: 0;
      top: calc(100% + 4px);
    }
  }
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  margin-bottom: 8px;
  flex-direction: row-reverse;
}

.header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.title {
  font-size: 18px;
  font-weight: 700;
  color: #000000;
  white-space: nowrap;
}

.line {
  height: 2px;
  background-color: #C8C8C8;
  width: 100%;
}

.validation-messages {
  margin-top: 20px;
}

.validation-message {
  padding: 12px;
  border-radius: 6px;
  font-weight: 500;
  margin-bottom: 10px;

  &.error {
    background-color: #fff8f8;
    border: 1px solid #ff4444;
    color: #ff4444;
  }

  &.info {
    background-color: #f0f8ff;
    border: 1px solid #007bff;
    color: #007bff;
  }
}

.debug-info {
  margin-top: 20px;
  padding: 15px;
  border: 1px solid #ccc;
  background-color: #f9f9f9;

  pre {
    font-size: 12px;
    white-space: pre-wrap;
  }

  button {
    margin: 10px 0;
    padding: 8px 16px;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
  }
}

.debug-toggle {
  margin-top: 20px;

  button {
    padding: 8px 16px;
    background-color: #6c757d;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
  }
}
</style>
